import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Tab,
  Tabs,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { apiService } from '../services/api';
import { runConnectDiagnostics } from '../services/connectDiagnostics';
import ConnectDiagnostics from './ConnectDiagnostics';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * PluginConnect
 *
 * Opened as a popup by the WordPress plugin onboarding wizard.
 * URL: /connect?site_domain=example.com&origin=https://account.improveseoplugin.com
 *
 * Flow:
 *   1. User signs in or signs up
 *   2. issueConnectToken is called with site_domain + Supabase JWT
 *   3. connect_token is sent back to the opener via postMessage
 *   4. Popup closes
 *
 * If window.opener is null (user navigated here directly), redirects to /dashboard.
 */
const PluginConnect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Params from the plugin ─────────────────────────────────────────────────
  // Read from URL params; fall back to sessionStorage so values survive a Google
  // OAuth redirect back here (the popup navigates away and loses URL params).
  const rawDomain = searchParams.get('site_domain')
    || sessionStorage.getItem('pluginConnect_domain')
    || '';
  // Normalise domain: trim + lowercase FIRST, then strip www. (order matters —
  // an uppercase "WWW." survives a case-sensitive strip done before lowercasing)
  const siteDomain = rawDomain.trim().toLowerCase().replace(/^www\./, '');
  // The WordPress admin origin passed by the plugin — used as postMessage target.
  // Falls back to '*' only if missing (direct navigation), which is safe because
  // the connect_token is single-use with a 5-min TTL.
  const openerOrigin = searchParams.get('origin')
    || sessionStorage.getItem('pluginConnect_origin')
    || '*';
  // WordPress admin URL to redirect back to when window.opener is unavailable
  // (Safari/WebKit drops the opener after the Google OAuth redirect, so postMessage
  // can't reach the WP window — we hand the single-use token back via the URL instead).
  const returnUrl = searchParams.get('return_url')
    || sessionStorage.getItem('pluginConnect_return_url')
    || '';

  // ── Local state ────────────────────────────────────────────────────────────
  // Honour ?tab= param: 0 = Sign In (default), 1 = Create Account
  const tabParam = parseInt(searchParams.get('tab') ?? '0', 10);
  const [tabValue, setTabValue] = useState(!isNaN(tabParam) && tabParam === 1 ? 1 : 0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('auth'); // 'auth' | 'connecting' | 'done'
  // Prevents double-trigger when Supabase fires TOKEN_REFRESHED right after INITIAL_SESSION
  // (both would see phase==='auth' before React re-renders, causing a connect loop)
  const connectingRef = useRef(false);
  // The auth-state listener auto-starts the connect exactly ONCE per session. This
  // is the hard stop against the retry storm: a deterministic failure (e.g. a 403
  // trial-rule rejection) must never re-trigger the connect on its own. User-initiated
  // sign-in (handleSignIn/SignUp/Google) resets this so a fresh attempt re-arms it.
  const autoConnectTriggeredRef = useRef(false);
  // Access token captured after auth, used by completeConnect after email verification.
  const sessionTokenRef = useRef(null);

  // ── Diagnostics ────────────────────────────────────────────────────────────
  const [connectStep, setConnectStep] = useState('');   // human label of current connect step
  const [connectStuck, setConnectStuck] = useState(false); // spinner exceeded the patience window
  const [diagReport, setDiagReport] = useState(null);
  const [diagRunning, setDiagRunning] = useState(false);

  const runDiagnostics = useCallback(async () => {
    setDiagRunning(true);
    try {
      const report = await runConnectDiagnostics();
      setDiagReport(report);
    } catch (_) {
      setDiagReport({ results: [], overall: 'fail', summary: null });
    } finally {
      setDiagRunning(false);
    }
  }, []);

  // ── Guard: if opened directly (not via the plugin), go to dashboard ────────
  // Don't bail when a connect is already in progress: after the Google OAuth
  // redirect, Safari/WebKit nulls window.opener even though we still need to
  // finish issuing the token (we fall back to a redirect in handlePostAuth).
  useEffect(() => {
    const pendingConnect = !!(siteDomain || sessionStorage.getItem('pluginConnect_domain'));
    if (!window.opener && !pendingConnect) {
      navigate('/', { replace: true });
    }
  }, [navigate, siteDomain]);

  // ── Persist params to sessionStorage so they survive a Google OAuth redirect ─
  useEffect(() => {
    if (siteDomain) sessionStorage.setItem('pluginConnect_domain', siteDomain);
    if (openerOrigin && openerOrigin !== '*') sessionStorage.setItem('pluginConnect_origin', openerOrigin);
    if (returnUrl) sessionStorage.setItem('pluginConnect_return_url', returnUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Complete the connect: issue the token and hand it back to WordPress ────
  const completeConnect = useCallback(async () => {
    setPhase('connecting');
    setError('');

    try {
      setConnectStep('Issuing a secure connection token…');
      const tokenData = await apiService.issueConnectToken(siteDomain, sessionTokenRef.current);

      setConnectStep('Returning to WordPress…');

      // Clean up sessionStorage now that the token is issued
      sessionStorage.removeItem('pluginConnect_domain');
      sessionStorage.removeItem('pluginConnect_origin');
      sessionStorage.removeItem('pluginConnect_return_url');

      if (window.opener) {
        // Fast path: hand the token to the WP admin window via postMessage and close.
        // Security: only postMessage to the expected origin (window.opener could be any page).
        window.opener.postMessage(
          {
            connect_token:  tokenData.connect_token,
            trial_status:   tokenData.trial_status,   // 'new' | 'reconnect'
            days_remaining: tokenData.days_remaining, // days left in the signup trial
          },
          openerOrigin  // target = WordPress admin origin, not CMS's own origin
        );
        setPhase('done');
        // Give the parent page a moment to process the message before closing
        setTimeout(() => {
          window.close();
        }, 800);
        return;
      }

      // Fallback (Safari/WebKit after Google OAuth): window.opener is gone, so
      // postMessage can't reach WP. Redirect back to the WP admin with the
      // single-use token (5-min TTL) in the URL; the plugin reads it on load.
      let target = null;
      try { target = new URL(returnUrl); } catch (e) { target = null; }

      // Only redirect to the origin the plugin told us to — prevents the CMS
      // being abused as an open redirector.
      if (target && (openerOrigin === '*' || target.origin === openerOrigin)) {
        target.searchParams.set('improveseo_connect_token', tokenData.connect_token);
        setPhase('done');
        window.location.replace(target.toString());
        return;
      }

      // No opener and no usable return URL — can't hand the token back.
      setPhase('auth');
      setError('Could not return to your site automatically. Please close this window and start the connection again from your WordPress dashboard.');
    } catch (err) {
      console.error('issueConnectToken failed:', err);
      connectingRef.current = false; // allow manual retry from the auth form
      setPhase('auth');
      // Map known error codes to friendly, actionable messages
      const codeMessages = {
        WEBSITE_LIMIT_REACHED: 'Your plan\'s website limit is reached. Remove a site or upgrade your plan, then try again.',
        CONCURRENT_REQUEST:    'A connection attempt is already in progress. Please wait a moment and try again.',
      };
      const code = err.response?.data?.code;
      setError(codeMessages[code] || err.response?.data?.error || err.message || 'Failed to connect. Please try again.');

      // For connectivity-class failures (network/timeout, not a business-rule
      // rejection), auto-run diagnostics so the user immediately sees the cause.
      const isConnectivityError =
        !err.response || err.isNetworkError || err.isTimeout ||
        [502, 503, 504].includes(err.response?.status);
      if (isConnectivityError && !codeMessages[code]) {
        runDiagnostics();
      }
    } finally {
      setConnectStep('');
    }
  }, [siteDomain, openerOrigin, returnUrl, runDiagnostics]);

  // ── After Supabase auth: sync the OAuth user record, then connect ──────────
  const handlePostAuth = useCallback(async (session) => {
    if (!session) return;
    if (connectingRef.current) return; // already in-flight — ignore duplicate event
    connectingRef.current = true;
    setError('');

    try {
      // For OAuth providers (Google etc.), sync the backend user record first.
      // New OAuth users entering via the popup bypass AuthContext.syncOAuthUser —
      // without this, issue-connect-token returns 404 "User account not found"
      // because the users table row may not exist yet.
      const provider = session.user?.app_metadata?.provider;
      if (provider && provider !== 'email') {
        setPhase('connecting');
        setConnectStep('Preparing your account…');
        await apiService.syncOAuthUser(provider, session.user);
        setConnectStep('');
      }

      // Trial + credits are granted at signup — connect straight away.
      sessionTokenRef.current = session.access_token;
      await completeConnect();
    } catch (err) {
      console.error('post-auth prep failed:', err);
      connectingRef.current = false; // allow retry from the auth form
      setPhase('auth');
      setError(err.response?.data?.error || err.message || 'Something went wrong. Please try again.');
    }
  }, [completeConnect]);

  // Keep a ref to the latest handlePostAuth so the auth-state listener can be
  // subscribed ONCE (empty deps) without going stale — see the listener effect below.
  const handlePostAuthRef = useRef(handlePostAuth);
  useEffect(() => { handlePostAuthRef.current = handlePostAuth; }, [handlePostAuth]);

  // ── Listen for auth state changes (covers OAuth redirect edge cases) ───────
  // CRITICAL: subscribe exactly ONCE (empty deps). Depending on `phase` here is a
  // trap — every phase change tears down and re-creates the subscription, and a new
  // onAuthStateChange subscription immediately RE-EMITS INITIAL_SESSION. With a
  // deterministic failure (e.g. a 403 trial rejection) that produced an infinite
  // loop: connect → 403 → phase='auth' → re-subscribe → re-emit → connect → …
  // hammering the server hundreds of times. We guard with a one-shot ref instead.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // TOKEN_REFRESHED is background maintenance — never a signal to initiate a connect.
      const isAuthEvent = event === 'SIGNED_IN' || event === 'INITIAL_SESSION';
      if (isAuthEvent && session && !autoConnectTriggeredRef.current) {
        autoConnectTriggeredRef.current = true; // one auto-attempt per session
        handlePostAuthRef.current(session);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Stuck-spinner watchdog ─────────────────────────────────────────────────
  // If "Connecting…" runs longer than 15s, treat it as stuck and auto-run
  // diagnostics so the user sees the likely cause instead of a dead spinner.
  useEffect(() => {
    if (phase !== 'connecting') {
      setConnectStuck(false);
      return;
    }
    const t = setTimeout(() => {
      setConnectStuck(true);
      runDiagnostics();
    }, 15000);
    return () => clearTimeout(t);
  }, [phase, runDiagnostics]);

  // ── Handle Google OAuth sign-in ───────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    setError('');
    autoConnectTriggeredRef.current = false; // re-arm one-shot connect for this fresh attempt
    // Persist params now — they will be gone after the OAuth redirect
    if (siteDomain) sessionStorage.setItem('pluginConnect_domain', siteDomain);
    if (openerOrigin && openerOrigin !== '*') sessionStorage.setItem('pluginConnect_origin', openerOrigin);
    if (returnUrl) sessionStorage.setItem('pluginConnect_return_url', returnUrl);
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Redirect back to /connect (not /auth/callback) so this component handles the session
        redirectTo: `${window.location.origin}/connect`,
      },
    });
  };

  // ── Handle Sign In ─────────────────────────────────────────────────────────
  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setDiagReport(null);
    autoConnectTriggeredRef.current = false; // re-arm one-shot connect for this fresh attempt

    // Detect Google-only accounts before attempting password login — gives a
    // clear message instead of Supabase's generic "Invalid login credentials".
    try {
      const eligibility = await apiService.verifyResetEligibility(email);
      if (eligibility.success && !eligibility.eligible && eligibility.reason === 'oauth_only') {
        const provider = eligibility.provider || 'Google';
        setError(`This account uses ${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-in. Please use the "Continue with Google" button below.`);
        setLoading(false);
        return;
      }
    } catch (_) {
      // verifyResetEligibility is best-effort; proceed with login attempt on failure
    }

    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });

    if (signInError) {
      setError(signInError.message || 'Invalid email or password.');
      setLoading(false);
      return;
    }

    if (!data.user?.email_confirmed_at) {
      setError('Please verify your email address before connecting.');
      await supabase.auth.signOut();
      setLoading(false);
      return;
    }

    // onAuthStateChange will fire and call handlePostAuth
    setLoading(false);
  };

  // ── Handle Sign Up ─────────────────────────────────────────────────────────
  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }
    setLoading(true);
    setError('');
    setDiagReport(null);
    autoConnectTriggeredRef.current = false; // re-arm one-shot connect for this fresh attempt

    try {
      // Create Supabase auth user via admin API (server-side) with auto-confirm
      // source:'plugin' triggers email_confirm:true so the user can sign in immediately
      const result = await apiService.signup({ email, name, password, source: 'plugin' });

      if (!result.success) {
        setError(result.error || 'Sign up failed. Please try again.');
        setLoading(false);
        return;
      }

      // Sign in immediately (email already confirmed by server)
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

      if (signInError) {
        setError('Account created. Please sign in with your new credentials.');
        setTabValue(0);
        setLoading(false);
        return;
      }

      // onAuthStateChange fires → handlePostAuth
      setLoading(false);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err?.response?.data?.error || err.message || 'Sign up failed. Please try again.');
      setLoading(false);
    }
  };

  // ── Render: done state ─────────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main' }} />
        <Typography variant="h6">Connected! Closing window…</Typography>
      </Box>
    );
  }

  // ── Render: issuing token ─────────────────────────────────────────────────
  if (phase === 'connecting') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Container maxWidth="xs">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary">Connecting your site…</Typography>
            {connectStep && (
              <Typography variant="caption" color="text.secondary">{connectStep}</Typography>
            )}

            {connectStuck && (
              <Card elevation={2} sx={{ width: '100%', mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    This is taking longer than expected
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    We're checking your device and network for anything blocking the connection.
                  </Typography>
                  <ConnectDiagnostics running={diagRunning} report={diagReport} onRun={runDiagnostics} compact />
                </CardContent>
              </Card>
            )}
          </Box>
        </Container>
      </Box>
    );
  }

  // ── Render: auth form ─────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Container maxWidth="xs">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Connect ImproveSEO
          </Typography>
          {siteDomain && (
            <Typography variant="body2" color="text.secondary">
              Connecting <strong>{siteDomain}</strong> to your account
            </Typography>
          )}
        </Box>

        <Card elevation={2}>
          <CardContent sx={{ p: 3 }}>
            <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setError(''); }} variant="fullWidth" sx={{ mb: 1 }}>
              <Tab label="Sign In" />
              <Tab label="Create Account" />
            </Tabs>

            {error && <Alert severity="error" sx={{ mt: 2, mb: 1 }}>{error}</Alert>}

            {(error || diagReport) && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <ConnectDiagnostics running={diagRunning} report={diagReport} onRun={runDiagnostics} />
              </Box>
            )}

            {/* ─── Sign In tab ─── */}
            <TabPanel value={tabValue} index={0}>
              <Box component="form" onSubmit={handleSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoComplete="email"
                  size="small"
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ py: 1.2 }}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In & Connect'}
                </Button>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    component="a"
                    href="/forgot-password"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    Forgot password?
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">OR</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  onClick={handleGoogleSignIn}
                  startIcon={
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      style={{ width: 18, height: 18 }}
                    />
                  }
                  sx={{
                    textTransform: 'none',
                    borderColor: '#D3D3D3',
                    color: 'text.primary',
                    '&:hover': { backgroundColor: '#f9f9f9', borderColor: '#aaa' },
                  }}
                >
                  Continue with Google
                </Button>
              </Box>
            </TabPanel>

            {/* ─── Sign Up tab ─── */}
            <TabPanel value={tabValue} index={1}>
              <Box component="form" onSubmit={handleSignUp} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Your Name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  autoComplete="name"
                />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  inputProps={{ minLength: 8 }}
                  helperText="Minimum 8 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreedToTerms}
                      onChange={e => setAgreedToTerms(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption">
                      I agree to the{' '}
                      <a href="https://improveseoplugin.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                      {' '}and{' '}
                      <a href="https://improveseoplugin.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
                    </Typography>
                  }
                />
                <Button type="submit" variant="contained" fullWidth disabled={loading || !agreedToTerms} sx={{ py: 1.2 }}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Account & Start Free Trial'}
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" color="text.secondary">OR</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                  onClick={handleGoogleSignIn}
                  startIcon={
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      style={{ width: 18, height: 18 }}
                    />
                  }
                  sx={{
                    textTransform: 'none',
                    borderColor: '#D3D3D3',
                    color: 'text.primary',
                    '&:hover': { backgroundColor: '#f9f9f9', borderColor: '#aaa' },
                  }}
                >
                  Continue with Google
                </Button>
              </Box>
            </TabPanel>

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
              Your 7-day free trial starts when you create your account.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default PluginConnect;
